import { Icon } from '@aibees/shared/components';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  InputLabel,
  Typography,
} from '@mui/material';
import { random } from 'lodash-es';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TaxIdRow } from './components/tax-id-row';
import * as Styled from './styles';
import * as SX from './sx';
import type { TaxIdFormProps as Props } from './types';
import { useTaxIdHandlers } from './use-tax-id-handlers';

export const TaxIdForm = memo(
  ({
    className,
    onChange,
    onRefetch,
    onDeleteTaxId,
    globalTaxIdError,
    onClearError,
  }: Props) => {
    const { control, watch, setValue } = useFormContext();
    const notify = useNotify();
    const taxIds = useMemo(() => watch('taxIds') ?? [], [watch]);
    const [rows, setRows] = useState<number[]>(() =>
      taxIds.length > 0 ? taxIds.map((_: unknown, idx: number) => idx) : [0]
    );
    const prevTaxIdsLength = useRef(taxIds.length);
    const initialTaxIds = useRef(taxIds);

    useEffect(() => {
      if (taxIds.length > 0 && taxIds.length > prevTaxIdsLength.current) {
        setRows(taxIds.map((_: unknown, idx: number) => idx));
      }
      prevTaxIdsLength.current = taxIds.length;

      if (
        taxIds.length > 0 &&
        taxIds.some((taxId: any) => taxId.type || taxId.value)
      ) {
        initialTaxIds.current = taxIds;
      }
    }, [taxIds.length, taxIds]);

    const handleAddRow = () => {
      if (rows.length >= 5) {
        notify.warning(
          'You can only add up to 5 VAT IDs',
          `You can only add up to 5 VAT IDs`,
          random(1, 100),
          {
            icon: 'location',
          }
        );
        return;
      }

      const newIndex = rows.length;
      setRows((prev) => [...prev, newIndex]);
      setValue(
        `taxIds.${newIndex}`,
        { type: '', value: '', existing: false },
        { shouldDirty: true }
      );
      onChange?.();
    };

    const { createRemoveHandler, getIsExisting } = useTaxIdHandlers(
      taxIds,
      setRows,
      notify,
      onRefetch,
      onDeleteTaxId,
      () => {
        const currentTaxIds = taxIds || [];
        const originalTaxIds = initialTaxIds.current || [];

        const isBackToOriginal =
          currentTaxIds.length === originalTaxIds.length &&
          currentTaxIds.every((currentTaxId: any, index: number) => {
            const originalTaxId = originalTaxIds[index];
            if (!originalTaxId) return true;

            return (
              currentTaxId.type?.trim() === originalTaxId.type?.trim() &&
              currentTaxId.value?.trim() === originalTaxId.value?.trim()
            );
          });
        onChange?.(!isBackToOriginal);
      },
      onClearError
    );

    return (
      <Styled.Wrapper
        className={classnames('TaxIdForm', className)}
        sx={SX.root}
      >
        {globalTaxIdError && (
          <Alert
            severity="error"
            icon={<Icon name="problem" size={20} />}
            sx={{ mt: 1 }}
          >
            <AlertTitle>Tax ID is invalid</AlertTitle>
            {globalTaxIdError}
          </Alert>
        )}
        <Typography variant="body2" color="grey.800" mt={2}>
          VAT Information
        </Typography>
        <InputLabel shrink sx={SX.label}>
          TAX ID
        </InputLabel>

        <Box sx={SX.body}>
          {rows.map((rowIndex) => (
            <TaxIdRow
              key={rowIndex}
              rowIndex={rowIndex}
              control={control}
              getIsExisting={getIsExisting}
              onRemove={createRemoveHandler(rowIndex)}
            />
          ))}
          <Box>
            <Button variant="outlined" size="small" onClick={handleAddRow}>
              Add another ID
            </Button>
          </Box>
        </Box>
      </Styled.Wrapper>
    );
  }
);

TaxIdForm.displayName = 'TaxIdForm';
